/**
 * features/terms/tabs/AssessmentsTab.tsx
 * ------------------------------------------------------------------
 * Per-term CA label configuration. Lifted from the old
 * AssessmentConfigPanel which showed all three terms in a grid;
 * here we scope to a single term, but the logic, validation, and
 * data-loss guard are identical (use the same lib/assessmentConfig
 * helpers as before — pure functions).
 *
 * Add/rename is always allowed. Reducing the count prompts for
 * confirmation if any of the removed labels have student-mark data.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, doc, getDocs } from 'firebase/firestore';
// Batch C: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Plus,
  Trash2,
  Save,
  AlertTriangle,
  Loader2,
  Check,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { SCHOOL, type TermKey } from '@/lib/school.config';
import {
  getCaLabels,
  validateCaLabels,
  findCaLabelsLosingData,
} from '@/lib/assessmentConfig';
import { Card } from '@/components/ui/primitives';
import type { MarksDocument } from '@/types';

interface Props {
  term: TermKey;
}

export default function AssessmentsTab({ term }: Props) {
  const { userData } = useAuth();
  const { assessmentConfigs } = useData();
  const [draft, setDraft] = useState<string[]>([]);
  const [labelsWithData, setLabelsWithData] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [confirmDrop, setConfirmDrop] = useState<string[] | null>(null);

  // Seed draft from configured / fallback labels for this term
  useEffect(() => {
    setDraft(getCaLabels(term, assessmentConfigs));
  }, [assessmentConfigs, term]);

  // Scan reportMarks for this term to know which labels have data
  useEffect(() => {
    let cancel = false;
    (async () => {
      setScanning(true);
      try {
        const snap = await getDocs(collection(db, COLLECTIONS.marks));
        const found = new Set<string>();
        snap.docs.forEach((d) => {
          const data = d.data() as MarksDocument;
          if (data.term !== term) return;
          for (const sid of Object.keys(data.entries ?? {})) {
            const ca = (data.entries as Record<string, { ca?: Record<string, unknown> }>)[sid]?.ca;
            if (!ca) continue;
            for (const k of Object.keys(ca)) {
              if (ca[k] !== '' && ca[k] !== null && ca[k] !== undefined) {
                found.add(k);
              }
            }
          }
        });
        if (!cancel) setLabelsWithData(found);
      } catch (e) {
        if (!cancel) setMsg({ ok: false, text: `Could not scan marks: ${(e as Error).message}` });
      } finally {
        if (!cancel) setScanning(false);
      }
    })();
    return () => { cancel = true; };
  }, [term]);

  function setLabel(idx: number, val: string) {
    setDraft((d) => d.map((l, i) => (i === idx ? val : l)));
  }
  function addLabel() {
    setDraft((d) => [...d, `CA${d.length + 1}`]);
  }
  function removeLabel(idx: number) {
    setDraft((d) => d.filter((_, i) => i !== idx));
  }

  async function save(force: boolean) {
    const next = draft.map((s) => s.trim());
    const validation = validateCaLabels(next);
    if (!validation.ok) {
      setMsg({ ok: false, text: validation.errors.join(' · ') });
      return;
    }
    const previous = getCaLabels(term, assessmentConfigs);
    const dropped = findCaLabelsLosingData(previous, next, labelsWithData);
    if (dropped.length > 0 && !force) {
      setConfirmDrop(dropped);
      return;
    }
    setBusy(true);
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: 'Saving CA labels…' });
    try {
      await setDoc(
        doc(db, COLLECTIONS.assessmentConfig, term),
        {
          id: term,
          term,
          caLabels: next,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid ?? '',
        },
        { merge: true },
      );
      setSaveModal({ show: true, status: 'success', message: `CA labels saved for ${SCHOOL.terms[term].label}.` });
      setConfirmDrop(null);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card className="p-5">
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            These labels are what teachers see in Mark Entry. The grading math doesn't care what the CAs are called — it averages whatever values are entered. The label IS the storage key, so reducing the count after data has been entered will prompt for confirmation per affected slot.
          </p>
          {scanning && (
            <p className="text-xs text-gray-400 mt-2 inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" />
              scanning existing marks…
            </p>
          )}
        </div>

        {msg && (
          <div className={`mb-4 text-sm rounded-xl px-3 py-2.5 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
          }`}>
            {msg.ok ? <Check size={15} /> : <AlertTriangle size={15} />}
            {msg.text}
          </div>
        )}

        <div className="space-y-2 max-w-md">
          {draft.map((label, idx) => {
            const hasMarks = labelsWithData.has(label);
            return (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 w-6 shrink-0">
                  #{idx + 1}
                </span>
                <input
                  value={label}
                  onChange={(e) => setLabel(idx, e.target.value)}
                  maxLength={40}
                  className="flex-1 px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                />
                <button
                  onClick={() => removeLabel(idx)}
                  disabled={draft.length === 1 || busy}
                  title={hasMarks ? 'This CA has data — confirmation required' : 'Remove this CA'}
                  className={`p-2 rounded-lg shrink-0 disabled:opacity-40 ${
                    hasMarks
                      ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                      : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-500'
                  }`}
                >
                  {hasMarks ? <AlertTriangle size={14} /> : <Trash2 size={14} />}
                </button>
              </div>
            );
          })}

          <button
            onClick={addLabel}
            disabled={busy || draft.length >= 8}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-500 hover:border-primary hover:text-primary disabled:opacity-50 transition-colors"
          >
            <Plus size={14} /> Add CA
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => save(false)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save {SCHOOL.terms[term].label}
          </button>
        </div>
      </Card>

      {/* Confirm-drop modal */}
      {confirmDrop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card glass className="w-full max-w-md p-6 animate-fade-in">
            <h2 className="text-lg font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <AlertTriangle size={18} /> Drop CAs with data?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
              These CAs have student-mark data:
            </p>
            <ul className="mt-2 space-y-1">
              {confirmDrop.map((d) => (
                <li key={d} className="text-sm font-mono px-3 py-1.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  {d}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-3">
              Saving will drop the slots from the config. The mark data stays in Firestore but becomes invisible — re-add a CA with the exact same label to restore it.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmDrop(null)}
                disabled={busy}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => save(true)}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                Drop anyway
              </button>
            </div>
          </Card>
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </>
  );
}
